import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import { fetchAllCalendarTasks } from "../api/calendarTransport";
import {
  updateCalendarTask,
  deleteCalendarTask,
} from "../api/taskMutations";
import {
  fetchPropertySummary,
} from "../../tasksList/api/properties";
import { createPropertySummaryStore } from "../../tasksList/lib/propertySummaryLoader";

import {
  parseCalendarSearchParams,
  buildCalendarSearchParams,
  buildTabContextParams,
  resolveCalendarStatusFilter,
  reconcileStatusShowDone,
} from "../lib/calendarCodec";
import {
  getLocalMonthLabel,
  formatMonthlyTaskCount,
  isCurrentLocalMonth,
  prevMonth,
  nextMonth,
  todayLocalMonth,
  todayLocalDate,
  monthRangeApiParams,
} from "../lib/calendarMonth";
import { buildCalendarGrid, WEEKDAY_HEADERS } from "../lib/calendarGrid";
import {
  groupTasksByLocalDay,
  getTasksForDay,
  formatLocalTime,
  formatLocalDate,
  isTaskOverdue,
  localDayFromDueDate,
} from "../lib/calendarGrouping";
import {
  getPriorityLabel,
  propertyTitleOrFallback,
  clientDisplayName,
} from "../../tasksList/lib/taskFormatting";
import { getCalendarStatusLabel } from "../lib/calendarStatusLabels";
import {
  VALID_STATUSES,
  VALID_PRIORITIES,
  PRIORITY_CHIPS,
} from "../../tasksList/lib/taskListQuery";

import TasksViewTabs from "../../tasksShared/TasksViewTabs";
import TaskWorkspaceHeader from "../../tasksShared/TaskWorkspaceHeader";
import CalendarTaskEditorModal from "../../tasksShared/widgets/TaskEditorModal";
import CalendarTaskDeleteDialog from "./CalendarTaskDeleteDialog";

const MAX_CHIPS = 3;

const STATUS_FILTER_OPTIONS = [
  { value: "todo", label: "Заплановано" },
  { value: "in_progress", label: "В роботі" },
  { value: "done", label: "Виконано" },
];

const WEEKEND_COL_INDEXES = new Set([5, 6]);

// ─── Task Chip ────────────────────────────────────────────────────
function TaskChip({ task }) {
  const done = task.status === "done";
  const overdue = isTaskOverdue(task);
  const time = formatLocalTime(task.due_date);
  const statusLabel = getCalendarStatusLabel(task.status || "todo");
  const priority = task.priority || "medium";
  const priorityLabel = getPriorityLabel(priority);

  let stateLabel = "";
  if (done) stateLabel = "Виконано";
  else if (overdue) stateLabel = "Прострочено";

  return (
    <div
      className={[
        "feature-tasks-calendar__chip",
        `feature-tasks-calendar__chip--priority-${priority}`,
        done ? "feature-tasks-calendar__chip--done" : "",
        overdue && !done ? "feature-tasks-calendar__chip--overdue" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      title={task.title}
      aria-label={[task.title, statusLabel, priorityLabel, stateLabel]
        .filter(Boolean)
        .join(", ")}
    >
      {time ? (
        <span className="feature-tasks-calendar__chip-time">{time}</span>
      ) : null}
      <span className="feature-tasks-calendar__chip-title">{task.title}</span>
      {stateLabel ? (
        <span className="feature-tasks-calendar__chip-state" aria-hidden="true">
          {done ? "✓" : "!"}
        </span>
      ) : null}
    </div>
  );
}

// ─── Day Cell ─────────────────────────────────────────────────────
function DayCell({ cell, tasks, isToday, isSelected, isWeekend, onSelect }) {
  if (!cell.isCurrentMonth) {
    return (
      <td
        className={[
          "feature-tasks-calendar__cell",
          "feature-tasks-calendar__cell--adjacent",
          isWeekend ? "feature-tasks-calendar__cell--weekend" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="gridcell"
        aria-hidden="true"
      >
        <span className="feature-tasks-calendar__cell-day" />
      </td>
    );
  }

  const count = tasks.length;
  const visibleTasks = tasks.slice(0, MAX_CHIPS);
  const overflow = count - MAX_CHIPS;

  const dayNumber = parseInt(cell.dateStr.split("-")[2], 10);
  const fullLabel = `${formatLocalDate(cell.dateStr)}, задач: ${count}`;

  return (
    <td
      className={[
        "feature-tasks-calendar__cell",
        isWeekend ? "feature-tasks-calendar__cell--weekend" : "",
        isToday ? "feature-tasks-calendar__cell--today" : "",
        isSelected ? "feature-tasks-calendar__cell--selected" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="gridcell"
      data-weekend={isWeekend ? "true" : undefined}
    >
      <button
        type="button"
        className="feature-tasks-calendar__day-select"
        onClick={() => onSelect(cell.dateStr)}
        aria-pressed={isSelected}
        aria-current={isToday ? "date" : undefined}
        aria-label={fullLabel}
      >
        <span className="feature-tasks-calendar__cell-day">{dayNumber}</span>
        {count > 0 ? (
          <span className="feature-tasks-calendar__cell-count" aria-hidden="true">
            {count}
          </span>
        ) : null}
        <span className="feature-tasks-calendar__cell-chips">
          {visibleTasks.map((task) => (
            <TaskChip key={task.id} task={task} />
          ))}
        </span>
        {overflow > 0 ? (
          <span
            className="feature-tasks-calendar__chip-overflow"
            aria-hidden="true"
          >
            + ще {overflow}
          </span>
        ) : null}
      </button>
    </td>
  );
}

// ─── Month Toolbar ─────────────────────────────────────────────────
function MonthToolbar({
  month,
  monthTaskCount,
  showSummary,
  onPrev,
  onToday,
  onNext,
  isTodaySelected,
}) {
  return (
    <div
      className="feature-tasks-calendar__toolbar"
      role="navigation"
      aria-label="Навігація по місяцях"
    >
      <div className="feature-tasks-calendar__toolbar-nav">
        <button
          type="button"
          className="action-btn feature-tasks-calendar__nav-btn"
          onClick={onPrev}
          aria-label="Попередній місяць"
        >
          ‹
        </button>
        <h2 className="feature-tasks-calendar__month-heading">
          {getLocalMonthLabel(month, "uk-UA")}
        </h2>
        <button
          type="button"
          className="action-btn feature-tasks-calendar__nav-btn"
          onClick={onNext}
          aria-label="Наступний місяць"
        >
          ›
        </button>
        <button
          type="button"
          className="action-btn feature-tasks-calendar__today-btn"
          onClick={onToday}
          disabled={isTodaySelected}
          aria-label="Сьогодні"
        >
          Сьогодні
        </button>
      </div>
      {showSummary ? (
        <p
          className="feature-tasks-calendar__toolbar-summary"
          data-testid="tasks-calendar-monthly-summary"
          aria-live="polite"
        >
          {formatMonthlyTaskCount(month, monthTaskCount, "uk-UA")}
        </p>
      ) : null}
    </div>
  );
}

function CompactFilterChips({
  legend,
  options,
  selected,
  onToggle,
  onClear,
  ariaLabel,
}) {
  const allActive = selected.length === 0;
  return (
    <fieldset className="feature-tasks-calendar__filter-group" aria-label={ariaLabel}>
      <legend className="feature-tasks-calendar__filter-legend">{legend}</legend>
      <div className="feature-tasks-calendar__filter-chips">
        <button
          type="button"
          className={[
            "feature-tasks-calendar__filter-chip",
            allActive ? "feature-tasks-calendar__filter-chip--active" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-pressed={allActive}
          onClick={onClear}
        >
          Усі
        </button>
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              className={[
                "feature-tasks-calendar__filter-chip",
                active ? "feature-tasks-calendar__filter-chip--active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-pressed={active}
              onClick={() => onToggle(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

// ─── Selected-day Agenda ───────────────────────────────────────────
function AgendaTaskRow({
  task,
  propertySummaries,
  pending,
  mutationError,
  onToggleComplete,
  onOpenEdit,
  onRequestDelete,
  menuOpen,
  onToggleMenu,
}) {
  const done = task.status === "done";
  const overdue = isTaskOverdue(task);
  const statusLabel = getCalendarStatusLabel(task.status || "todo");
  const priorityLabel = getPriorityLabel(task.priority || "medium");
  const time = formatLocalTime(task.due_date);
  const propertyId = task.property;
  const summaryEntry =
    propertyId != null ? propertySummaries.get(propertyId) : null;
  const propertyLabel =
    propertyId != null
      ? summaryEntry && summaryEntry.status === "loaded"
        ? propertyTitleOrFallback(summaryEntry.data, propertyId)
        : propertyTitleOrFallback(null, propertyId)
      : null;

  const clientLabel = task.client
    ? clientDisplayName(task.client, task.client.id)
    : null;

  return (
    <li
      className={[
        "feature-tasks-calendar__agenda-row",
        done ? "feature-tasks-calendar__agenda-row--done" : "",
        overdue && !done ? "feature-tasks-calendar__agenda-row--overdue" : "",
        pending ? "feature-tasks-calendar__agenda-row--pending" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-task-id={task.id}
    >
      <div className="feature-tasks-calendar__agenda-row-main">
        <button
          type="button"
          className="feature-tasks-calendar__complete-btn"
          onClick={() => onToggleComplete(task)}
          disabled={pending}
          aria-pressed={done}
          aria-label={
            done
              ? `Позначити «${task.title}» як заплановану`
              : `Позначити «${task.title}» як виконану`
          }
        >
          <span aria-hidden="true">{done ? "✓" : ""}</span>
        </button>

        <div className="feature-tasks-calendar__agenda-body">
          <span className="feature-tasks-calendar__agenda-time">
            {time || "—"}
          </span>
          <div className="feature-tasks-calendar__agenda-main">
            <button
              type="button"
              className="feature-tasks-calendar__agenda-open"
              onClick={() => onOpenEdit(task)}
              disabled={pending}
              aria-label={`Відкрити задачу «${task.title}»`}
            >
              <span className="feature-tasks-calendar__agenda-row-title">
                {task.title}
              </span>
            </button>
            <div className="feature-tasks-calendar__agenda-context">
              {clientLabel ? (
                <span className="feature-tasks-calendar__agenda-context-item">
                  {clientLabel}
                </span>
              ) : null}
              {propertyLabel ? (
                <span className="feature-tasks-calendar__agenda-context-item">
                  {propertyLabel}
                </span>
              ) : null}
              {!clientLabel && !propertyLabel ? (
                <span className="feature-tasks-calendar__agenda-context-item">
                  —
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="feature-tasks-calendar__agenda-meta">
          <span
            className={[
              "feature-tasks-calendar__agenda-priority",
              `feature-tasks-calendar__agenda-priority--${task.priority || "medium"}`,
            ].join(" ")}
            title={priorityLabel}
          >
            <span
              className={[
                "feature-tasks-calendar__priority-dot",
                `feature-tasks-calendar__priority-dot--${task.priority || "medium"}`,
              ].join(" ")}
              aria-hidden="true"
            />
            <span className="feature-tasks-calendar__agenda-priority-label">
              {priorityLabel}
            </span>
          </span>
          <span className="feature-tasks-calendar__agenda-status">
            <span className="feature-tasks-calendar__agenda-badge feature-tasks-calendar__agenda-badge--status">
              {statusLabel}
            </span>
            {overdue && !done ? (
              <span className="feature-tasks-calendar__agenda-badge feature-tasks-calendar__agenda-badge--overdue">
                Прострочено
              </span>
            ) : null}
            {done ? (
              <span className="feature-tasks-calendar__agenda-badge feature-tasks-calendar__agenda-badge--done">
                Виконано
              </span>
            ) : null}
          </span>
        </div>

        <div className="feature-tasks-calendar__row-menu">
          <button
            type="button"
            className="feature-tasks-calendar__menu-trigger"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Дії для задачі «${task.title}»`}
            onClick={(event) => onToggleMenu(task.id, event.currentTarget)}
            disabled={pending}
          >
            <i
              className="fa-solid fa-ellipsis-vertical"
              aria-hidden="true"
            />
          </button>
          {menuOpen ? (
            <div
              className="feature-tasks-calendar__menu"
              role="menu"
              aria-label={`Меню задачі «${task.title}»`}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => onOpenEdit(task)}
              >
                Редагувати
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => onRequestDelete(task)}
              >
                Видалити
              </button>
            </div>
          ) : null}
        </div>
      </div>
      {mutationError ? (
        <p
          className="feature-tasks-calendar__mutation-error"
          role="alert"
          aria-live="assertive"
        >
          {mutationError}
        </p>
      ) : null}
    </li>
  );
}

function SelectedDayAgenda({
  day,
  tasks,
  propertySummaries,
  pendingTaskIds,
  taskErrors,
  openMenuTaskId,
  onToggleMenu,
  onToggleComplete,
  onOpenEdit,
  onRequestDelete,
}) {
  if (!day) {
    return (
      <aside
        className="feature-tasks-calendar__agenda"
        aria-label="Задачі вибраного дня"
      >
        <p className="feature-tasks-calendar__agenda-placeholder">
          Оберіть день, щоб переглянути задачі.
        </p>
      </aside>
    );
  }

  const title = `Задачі на ${formatLocalDate(day)}`;

  return (
    <aside
      className="feature-tasks-calendar__agenda"
      aria-label="Задачі вибраного дня"
    >
      <div className="feature-tasks-calendar__agenda-head">
        <h3 className="feature-tasks-calendar__agenda-heading">{title}</h3>
        <span
          className="feature-tasks-calendar__agenda-count"
          data-testid="tasks-calendar-agenda-count"
        >
          {tasks.length} задач
        </span>
      </div>
      <div className="feature-tasks-calendar__agenda-scroll">
        {tasks.length === 0 ? (
          <p className="feature-tasks-calendar__agenda-empty">
            На цей день задач із дедлайном немає.
          </p>
        ) : (
          <ol className="feature-tasks-calendar__agenda-list">
            {tasks.map((task) => (
              <AgendaTaskRow
                key={task.id}
                task={task}
                propertySummaries={propertySummaries}
                pending={pendingTaskIds.has(task.id)}
                mutationError={taskErrors[task.id] || null}
                onToggleComplete={onToggleComplete}
                onOpenEdit={onOpenEdit}
                onRequestDelete={onRequestDelete}
                menuOpen={openMenuTaskId === task.id}
                onToggleMenu={onToggleMenu}
              />
            ))}
          </ol>
        )}
      </div>
    </aside>
  );
}

function NoDeadlineBanner({ listHref }) {
  return (
    <p className="feature-tasks-calendar__no-deadline-info">
      Задачі без дедлайну відображаються у{" "}
      <Link to={listHref} className="feature-tasks-list__link">
        списку задач
      </Link>
      .
    </p>
  );
}

function taskBelongsInMonthSnapshot(task, month, showDone, statuses, priorities) {
  if (!task?.due_date) return false;
  const day = localDayFromDueDate(task.due_date);
  if (!day || !day.startsWith(`${month}-`)) return false;
  const statusList = resolveCalendarStatusFilter(showDone, statuses);
  if (!statusList.includes(task.status || "todo")) return false;
  if (priorities.length > 0 && !priorities.includes(task.priority || "medium")) {
    return false;
  }
  return true;
}

// ─── Main Screen Content ──────────────────────────────────────────
function TasksCalendarScreenContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const propertySummaryStoreRef = useRef(null);
  const loadGenerationRef = useRef(0);
  const mutationGenerationRef = useRef(0);
  const activeRequestControllerRef = useRef(null);
  const pendingMutationsRef = useRef(new Set());
  if (!propertySummaryStoreRef.current) {
    propertySummaryStoreRef.current = createPropertySummaryStore();
  }

  const params = useMemo(
    () => parseCalendarSearchParams(searchParams),
    [searchParams],
  );

  useEffect(() => {
    if (
      params.needsMonthNormalize ||
      params.needsDayNormalize ||
      params.needsShowDoneNormalize ||
      params.needsClientNormalize ||
      params.needsPropertyNormalize ||
      params.needsStatusNormalize ||
      params.needsPriorityNormalize
    ) {
      const normalized = buildCalendarSearchParams({
        month: params.month,
        day: params.day,
        showDone: params.showDone,
        clientId: params.clientId,
        propertyId: params.propertyId,
        statuses: params.statuses,
        priorities: params.priorities,
      });
      setSearchParams(normalized, { replace: true });
    }
  }, [params, setSearchParams]);

  const needsUrlNormalize =
    params.needsMonthNormalize ||
    params.needsDayNormalize ||
    params.needsShowDoneNormalize ||
    params.needsClientNormalize ||
    params.needsPropertyNormalize ||
    params.needsStatusNormalize ||
    params.needsPriorityNormalize;

  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [monthTasks, setMonthTasks] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [propertySummaries, setPropertySummaries] = useState(new Map());
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  const [pendingTaskIds, setPendingTaskIds] = useState(() => new Set());
  const [taskErrors, setTaskErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState(null);
  const [openMenuTaskId, setOpenMenuTaskId] = useState(null);
  const openMenuTriggerRef = useRef(null);

  const closeAgendaMenu = useCallback((options = {}) => {
    const { restoreFocus = true } = options;
    const trigger = openMenuTriggerRef.current;
    setOpenMenuTaskId(null);
    openMenuTriggerRef.current = null;
    if (restoreFocus && trigger && typeof trigger.focus === "function") {
      // Restore after React closes the menu so focus is not lost.
      window.setTimeout(() => {
        try {
          trigger.focus();
        } catch (_err) {
          /* trigger may have unmounted with the row */
        }
      }, 0);
    }
  }, []);

  const [editorState, setEditorState] = useState({
    open: false,
    mode: "create",
    task: null,
    prefillDay: null,
    propertyLabel: "",
  });
  const [deleteState, setDeleteState] = useState({
    open: false,
    task: null,
    pending: false,
    error: null,
  });

  const {
    month,
    day,
    showDone,
    clientId,
    propertyId,
    statuses,
    priorities,
  } = params;

  const gridCells = useMemo(() => buildCalendarGrid(month), [month]);
  const { byDay } = useMemo(
    () => groupTasksByLocalDay(monthTasks),
    [monthTasks],
  );
  const todayStr = useMemo(() => todayLocalDate(), []);

  const selectedDayTasks = useMemo(
    () => (day ? getTasksForDay(byDay, day) : []),
    [byDay, day],
  );

  const listHref = useMemo(() => {
    const ctx = buildTabContextParams({ clientId, propertyId });
    const qs = ctx.toString();
    return qs ? `/tasks?${qs}` : "/tasks";
  }, [clientId, propertyId]);

  const applyParams = useCallback(
    (patch) => {
      const next = buildCalendarSearchParams({
        month: patch.month !== undefined ? patch.month : month,
        day: patch.day !== undefined ? patch.day : day,
        showDone: patch.showDone !== undefined ? patch.showDone : showDone,
        clientId: patch.clientId !== undefined ? patch.clientId : clientId,
        propertyId:
          patch.propertyId !== undefined ? patch.propertyId : propertyId,
        statuses: patch.statuses !== undefined ? patch.statuses : statuses,
        priorities:
          patch.priorities !== undefined ? patch.priorities : priorities,
      });
      setSearchParams(next);
    },
    [
      month,
      day,
      showDone,
      clientId,
      propertyId,
      statuses,
      priorities,
      setSearchParams,
    ],
  );

  const handlePrevMonth = () => {
    mutationGenerationRef.current += 1;
    applyParams({ month: prevMonth(month), day: null });
  };
  const handleNextMonth = () => {
    mutationGenerationRef.current += 1;
    applyParams({ month: nextMonth(month), day: null });
  };
  const handleToday = () => {
    const todayMonth = todayLocalMonth();
    const todayDate = todayLocalDate();
    if (todayMonth === month && day === todayDate) return;
    if (todayMonth !== month) {
      mutationGenerationRef.current += 1;
    }
    applyParams({ month: todayMonth, day: todayDate });
  };
  const handleSelectDay = (dateStr) => applyParams({ day: dateStr });
  const handleShowDoneToggle = () => {
    mutationGenerationRef.current += 1;
    const nextShowDone = !showDone;
    if (!nextShowDone) {
      applyParams({
        showDone: false,
        statuses: statuses.filter((status) => status !== "done"),
      });
      return;
    }
    applyParams({ showDone: true });
  };

  const toggleEnumFilter = (key, value, allowed) => {
    mutationGenerationRef.current += 1;
    if (key === "priorities") {
      const next = priorities.includes(value)
        ? priorities.filter((item) => item !== value)
        : [...priorities, value].filter((item) => allowed.includes(item));
      applyParams({ priorities: next });
      return;
    }
    const nextStatuses = statuses.includes(value)
      ? statuses.filter((item) => item !== value)
      : [...statuses, value].filter((item) => allowed.includes(item));
    const reconciled = reconcileStatusShowDone(
      value === "done" && !statuses.includes("done") ? true : showDone,
      nextStatuses,
    );
    applyParams({
      statuses: reconciled.statuses,
      showDone: reconciled.showDone,
    });
  };

  const clearEnumFilter = (key) => {
    if (key === "priorities") {
      if (priorities.length === 0) return;
      mutationGenerationRef.current += 1;
      applyParams({ priorities: [] });
      return;
    }
    if (statuses.length === 0) return;
    mutationGenerationRef.current += 1;
    applyParams({
      statuses: [],
      showDone,
    });
  };

  const filtersActive =
    statuses.length > 0 || priorities.length > 0 || showDone;

  const handleResetFilters = () => {
    if (!filtersActive) return;
    mutationGenerationRef.current += 1;
    applyParams({
      statuses: [],
      priorities: [],
      showDone: false,
    });
  };

  const buildMonthApiParams = useCallback(() => {
    const rangeParams = monthRangeApiParams(month);
    const statusList = resolveCalendarStatusFilter(showDone, statuses);
    const apiParams = {
      ...rangeParams,
      status: statusList,
    };
    if (priorities.length > 0) {
      apiParams.priority = priorities;
    }
    if (clientId != null) apiParams.client = clientId;
    if (propertyId != null) apiParams.property = propertyId;
    return apiParams;
  }, [month, showDone, statuses, priorities, clientId, propertyId]);

  const reconcileTask = useCallback(
    (task) => {
      setMonthTasks((prev) => {
        const belongs = taskBelongsInMonthSnapshot(
          task,
          month,
          showDone,
          statuses,
          priorities,
        );
        const without = prev.filter((row) => row.id !== task.id);
        if (!belongs) return without;
        return [...without, task];
      });
    },
    [month, showDone, statuses, priorities],
  );

  const removeTaskLocally = useCallback((taskId) => {
    setMonthTasks((prev) => prev.filter((row) => row.id !== taskId));
  }, []);

  const loadCalendar = useCallback(
    async ({ signal, mode = "initial", generation } = {}) => {
      const propertyStore = propertySummaryStoreRef.current;

      if (mode === "initial") {
        setLoadState("loading");
        setLoadError(null);
        setMonthTasks([]);
        setPageCount(0);
        setPropertySummaries(new Map());
        propertyStore.reset();
      } else if (mode === "refresh") {
        setRefreshing(true);
        setRefreshError(null);
      }

      const apiParams = buildMonthApiParams();
      const result = await fetchAllCalendarTasks(apiParams, { signal });

      if (signal && signal.aborted) return { ok: false, aborted: true };
      if (generation != null && generation !== loadGenerationRef.current) {
        return { ok: false, stale: true };
      }

      if (!result.ok) {
        if (result.aborted) return { ok: false, aborted: true };
        const message = result.error || "Не вдалося завантажити календар задач";
        if (mode === "refresh") {
          setRefreshError(`${message}. Поточні дані збережено.`);
          return { ok: false };
        }
        setMonthTasks([]);
        setPageCount(0);
        setPropertySummaries(new Map());
        setLoadError(message);
        setLoadState("error");
        return { ok: false };
      }

      if (generation != null && generation !== loadGenerationRef.current) {
        return { ok: false, stale: true };
      }

      setMonthTasks(result.tasks);
      setPageCount(result.pageCount);
      setLoadState("loaded");
      setRefreshError(null);
      return { ok: true };
    },
    [buildMonthApiParams],
  );

  const beginCalendarRequest = useCallback(() => {
    if (activeRequestControllerRef.current) {
      activeRequestControllerRef.current.abort();
    }
    const controller = new AbortController();
    activeRequestControllerRef.current = controller;
    return controller;
  }, []);

  const clearCalendarRequest = useCallback((controller) => {
    if (activeRequestControllerRef.current === controller) {
      activeRequestControllerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (activeRequestControllerRef.current) {
        activeRequestControllerRef.current.abort();
        activeRequestControllerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (needsUrlNormalize) return undefined;

    loadGenerationRef.current += 1;
    mutationGenerationRef.current += 1;
    const generation = loadGenerationRef.current;
    const controller = beginCalendarRequest();
    loadCalendar({ signal: controller.signal, mode: "initial", generation }).finally(
      () => {
        clearCalendarRequest(controller);
      },
    );
    return () => {
      controller.abort();
      clearCalendarRequest(controller);
    };
  }, [
    needsUrlNormalize,
    loadCalendar,
    month,
    showDone,
    statuses,
    priorities,
    clientId,
    propertyId,
    beginCalendarRequest,
    clearCalendarRequest,
  ]);

  useEffect(() => {
    if (!day || selectedDayTasks.length === 0) return undefined;
    const propertyStore = propertySummaryStoreRef.current;
    const propertyIds = [
      ...new Set(
        selectedDayTasks
          .map((t) => t.property)
          .filter((id) => id != null),
      ),
    ];
    if (propertyIds.length === 0) return undefined;

    const controller = new AbortController();

    propertyStore
      .loadForPropertyIds(propertyIds, (id) =>
        fetchPropertySummary(id, { signal: controller.signal }),
      { signal: controller.signal })
      .then((summaryMap) => {
        if (controller.signal.aborted) return;
        setPropertySummaries((prev) => {
          const next = new Map(prev);
          for (const [id, entry] of summaryMap) {
            next.set(id, entry);
          }
          return next;
        });
      })
      .catch(() => {});

    return () => controller.abort();
  }, [day, selectedDayTasks]);

  useEffect(() => {
    propertySummaryStoreRef.current.reset();
    setPropertySummaries(new Map());
  }, [month, clientId, propertyId]);

  useEffect(() => {
    if (openMenuTaskId == null) return undefined;
    const handlePointer = (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(".feature-tasks-calendar__row-menu")) return;
      closeAgendaMenu({ restoreFocus: true });
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeAgendaMenu({ restoreFocus: true });
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuTaskId, closeAgendaMenu]);

  const handleRefresh = () => {
    if (refreshing) return;
    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;
    const controller = beginCalendarRequest();
    loadCalendar({
      signal: controller.signal,
      mode: "refresh",
      generation,
    }).finally(() => {
      setRefreshing(false);
      clearCalendarRequest(controller);
    });
  };

  const handleRetry = () => {
    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;
    const controller = beginCalendarRequest();
    loadCalendar({ signal: controller.signal, mode: "initial", generation }).finally(
      () => {
        clearCalendarRequest(controller);
      },
    );
  };

  const openCreateEditor = (prefillDay = null) => {
    closeAgendaMenu({ restoreFocus: false });
    setEditorState({
      open: true,
      mode: "create",
      task: null,
      prefillDay: prefillDay || day || todayStr,
      propertyLabel: "",
    });
  };

  const openEditEditor = (task) => {
    closeAgendaMenu({ restoreFocus: false });
    let propertyLabel = "";
    if (task.property != null) {
      const summaryEntry = propertySummaries.get(task.property);
      propertyLabel =
        summaryEntry && summaryEntry.status === "loaded"
          ? propertyTitleOrFallback(summaryEntry.data, task.property)
          : propertyTitleOrFallback(null, task.property);
    }
    setEditorState({
      open: true,
      mode: "edit",
      task,
      prefillDay: null,
      propertyLabel,
    });
  };

  const closeEditor = () => {
    setEditorState({
      open: false,
      mode: "create",
      task: null,
      prefillDay: null,
      propertyLabel: "",
    });
  };

  const handleEditorSaved = (saved, meta = {}) => {
    const generationAtSave = mutationGenerationRef.current;
    const savedMode = meta.mode || "create";
    closeEditor();
    if (generationAtSave !== mutationGenerationRef.current) return;

    const taskDay = localDayFromDueDate(saved.due_date);
    const taskMonth = taskDay ? taskDay.slice(0, 7) : null;
    const belongs = taskBelongsInMonthSnapshot(
      saved,
      month,
      showDone,
      statuses,
      priorities,
    );
    const navigatesToOtherMonth =
      Boolean(taskMonth) && taskMonth !== month;

    setStatusMessage(
      savedMode === "edit" ? "Задачу оновлено." : "Задачу створено.",
    );

    if (navigatesToOtherMonth) {
      // Single navigation → URL effect loads the new month once.
      // Do not refresh the obsolete snapshot first.
      if (savedMode === "edit") {
        removeTaskLocally(saved.id);
      }
      applyParams({ day: taskDay, month: taskMonth });
      return;
    }

    if (belongs) {
      reconcileTask(saved);
      if (taskDay && taskDay !== day) {
        applyParams({ day: taskDay });
      }
      return;
    }

    // Same month but excluded by active status/priority filters:
    // do not insert against filters and do not refresh the same snapshot.
    if (savedMode === "edit") {
      removeTaskLocally(saved.id);
    }
  };

  const setTaskPending = (taskId, pending) => {
    setPendingTaskIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(taskId);
      else next.delete(taskId);
      return next;
    });
  };

  const handleToggleComplete = async (task) => {
    if (pendingMutationsRef.current.has(task.id)) return;
    const nextStatus = task.status === "done" ? "todo" : "done";
    const previous = task;
    const generationAtStart = mutationGenerationRef.current;

    pendingMutationsRef.current.add(task.id);
    setTaskPending(task.id, true);
    setTaskErrors((prev) => {
      if (!prev[task.id]) return prev;
      const out = { ...prev };
      delete out[task.id];
      return out;
    });

    // Optimistic local update
    reconcileTask({ ...task, status: nextStatus });

    try {
      const saved = await updateCalendarTask(task.id, { status: nextStatus });
      if (generationAtStart !== mutationGenerationRef.current) return;
      reconcileTask(saved);
      setStatusMessage(
        nextStatus === "done"
          ? "Задачу позначено виконаною."
          : "Виконання скасовано.",
      );
    } catch (err) {
      if (generationAtStart !== mutationGenerationRef.current) return;
      reconcileTask(previous);
      setTaskErrors((prev) => ({
        ...prev,
        [task.id]: messageFromError(err) || "Не вдалося оновити статус.",
      }));
    } finally {
      pendingMutationsRef.current.delete(task.id);
      setTaskPending(task.id, false);
    }
  };

  const handleRequestDelete = (task) => {
    closeAgendaMenu({ restoreFocus: false });
    setDeleteState({
      open: true,
      task,
      pending: false,
      error: null,
    });
  };

  const handleCancelDelete = () => {
    if (deleteState.pending) return;
    setDeleteState({
      open: false,
      task: null,
      pending: false,
      error: null,
    });
  };

  const handleConfirmDelete = async () => {
    const task = deleteState.task;
    if (!task || deleteState.pending) return;
    if (pendingMutationsRef.current.has(`delete-${task.id}`)) return;

    const generationAtStart = mutationGenerationRef.current;
    pendingMutationsRef.current.add(`delete-${task.id}`);
    setDeleteState((prev) => ({ ...prev, pending: true, error: null }));

    try {
      await deleteCalendarTask(task.id);
      if (generationAtStart !== mutationGenerationRef.current) {
        setDeleteState({
          open: false,
          task: null,
          pending: false,
          error: null,
        });
        return;
      }
      removeTaskLocally(task.id);
      setDeleteState({
        open: false,
        task: null,
        pending: false,
        error: null,
      });
      setStatusMessage("Задачу видалено.");
    } catch (err) {
      if (generationAtStart !== mutationGenerationRef.current) return;
      setDeleteState((prev) => ({
        ...prev,
        pending: false,
        error: messageFromError(err) || "Не вдалося видалити задачу.",
      }));
    } finally {
      pendingMutationsRef.current.delete(`delete-${task.id}`);
    }
  };

  const showInitialLoading = loadState === "loading";
  const showPrimaryError = loadState === "error";
  const showCalendar = loadState === "loaded";
  const isEmpty = showCalendar && monthTasks.length === 0;
  const isTodaySelected = isCurrentLocalMonth(month) && day === todayStr;
  const monthLabel = getLocalMonthLabel(month, "uk-UA");

  return (
    <div
      className="feature-tasks-calendar"
      data-feature="tasks-calendar"
      data-workspace="writable"
      data-layout="full-width-stack"
    >
      <TaskWorkspaceHeader
        subtitle="Календар дедлайнів і робочий простір ваших задач."
        onCreate={() => openCreateEditor(day || todayStr)}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        refreshDisabled={loadState === "loading"}
      />

      <TasksViewTabs />

      <div
        className="feature-tasks-calendar__live"
        role="status"
        aria-live="polite"
      >
        {statusMessage}
      </div>

      {refreshError ? (
        <div
          className="feature-tasks-list__refresh-error"
          role="alert"
          aria-live="polite"
        >
          {refreshError}
        </div>
      ) : null}

      {showInitialLoading ? (
        <div className="feature-tasks-calendar__state">
          <Spinner size="lg" label="Завантаження календаря задач" />
        </div>
      ) : null}

      {showPrimaryError ? (
        <div className="feature-tasks-calendar__state">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити календар задач"
            description={loadError || "Спробуйте оновити сторінку."}
            size="md"
            primaryAction={
              <button type="button" className="action-btn" onClick={handleRetry}>
                Повторити
              </button>
            }
          />
        </div>
      ) : null}

      {!showInitialLoading && !showPrimaryError ? (
        <div
          className="feature-tasks-calendar__workspace"
          data-testid="tasks-calendar-workspace"
          data-page-count={showCalendar ? pageCount : undefined}
        >
          <div
            className="feature-tasks-calendar__controls feature-tasks-calendar__controls-surface"
            data-testid="tasks-calendar-controls-surface"
          >
            <MonthToolbar
              month={month}
              monthTaskCount={monthTasks.length}
              showSummary={showCalendar}
              onPrev={handlePrevMonth}
              onNext={handleNextMonth}
              onToday={handleToday}
              isTodaySelected={isTodaySelected}
            />
          </div>

          <div
            className="feature-tasks-calendar__filter-tray"
            data-testid="tasks-calendar-filter-tray"
          >
            <div className="feature-tasks-calendar__filters">
              <CompactFilterChips
                legend="Статус"
                ariaLabel="Фільтр статусу"
                options={STATUS_FILTER_OPTIONS.filter((opt) =>
                  VALID_STATUSES.includes(opt.value),
                )}
                selected={statuses}
                onToggle={(value) =>
                  toggleEnumFilter("statuses", value, VALID_STATUSES)
                }
                onClear={() => clearEnumFilter("statuses")}
              />
              <CompactFilterChips
                legend="Пріоритет"
                ariaLabel="Фільтр пріоритету"
                options={PRIORITY_CHIPS.filter((opt) =>
                  VALID_PRIORITIES.includes(opt.value),
                )}
                selected={priorities}
                onToggle={(value) =>
                  toggleEnumFilter("priorities", value, VALID_PRIORITIES)
                }
                onClear={() => clearEnumFilter("priorities")}
              />
              <label className="feature-tasks-calendar__done-toggle">
                <input
                  type="checkbox"
                  checked={showDone}
                  onChange={handleShowDoneToggle}
                  aria-label="Показувати виконані"
                />
                <span>Показувати виконані</span>
              </label>
            </div>
            {filtersActive ? (
              <button
                type="button"
                className="feature-tasks-calendar__filters-reset"
                onClick={handleResetFilters}
              >
                Скинути фільтри
              </button>
            ) : null}
          </div>

          {showCalendar && isEmpty ? (
            <p className="feature-tasks-calendar__empty-month">
              У цьому місяці задач із дедлайном немає
            </p>
          ) : null}

          {showCalendar ? (
            <div className="feature-tasks-calendar__layout">
              <div className="feature-tasks-calendar__grid-wrap">
                <table
                  className="feature-tasks-calendar__grid"
                  role="grid"
                  aria-label={`Календар задач: ${monthLabel}`}
                >
                  <caption className="visually-hidden">
                    {`Календар задач: ${monthLabel}`}
                  </caption>
                  <thead>
                    <tr>
                      {WEEKDAY_HEADERS.map((hdr, colIdx) => (
                        <th
                          key={hdr}
                          scope="col"
                          abbr={hdr}
                          className={[
                            "feature-tasks-calendar__wday",
                            WEEKEND_COL_INDEXES.has(colIdx)
                              ? "feature-tasks-calendar__wday--weekend"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {hdr}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(
                      { length: gridCells.length / 7 },
                      (_, rowIdx) => (
                        <tr key={rowIdx}>
                          {gridCells
                            .slice(rowIdx * 7, rowIdx * 7 + 7)
                            .map((cell, colIdx) => (
                              <DayCell
                                key={cell.dateStr || `${rowIdx}-${colIdx}-adj`}
                                cell={cell}
                                tasks={
                                  cell.isCurrentMonth
                                    ? getTasksForDay(byDay, cell.dateStr)
                                    : []
                                }
                                isToday={cell.dateStr === todayStr}
                                isSelected={cell.dateStr === day}
                                isWeekend={WEEKEND_COL_INDEXES.has(colIdx)}
                                onSelect={handleSelectDay}
                              />
                            ))}
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>

              <SelectedDayAgenda
                day={day}
                tasks={selectedDayTasks}
                propertySummaries={propertySummaries}
                pendingTaskIds={pendingTaskIds}
                taskErrors={taskErrors}
                openMenuTaskId={openMenuTaskId}
                onToggleMenu={(taskId, triggerEl) => {
                  if (openMenuTaskId === taskId) {
                    closeAgendaMenu({ restoreFocus: true });
                    return;
                  }
                  openMenuTriggerRef.current = triggerEl || null;
                  setOpenMenuTaskId(taskId);
                }}
                onToggleComplete={handleToggleComplete}
                onOpenEdit={openEditEditor}
                onRequestDelete={handleRequestDelete}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {showCalendar ? <NoDeadlineBanner listHref={listHref} /> : null}

      <CalendarTaskEditorModal
        open={editorState.open}
        mode={editorState.mode}
        task={editorState.task}
        prefillDay={editorState.prefillDay}
        propertyLabel={editorState.propertyLabel}
        onClose={closeEditor}
        onSaved={handleEditorSaved}
        mutationGenerationRef={mutationGenerationRef}
      />

      <CalendarTaskDeleteDialog
        open={deleteState.open}
        task={deleteState.task}
        pending={deleteState.pending}
        error={deleteState.error}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

export default TasksCalendarScreenContent;
